import { cn } from "@omni/utils/styles";
import { ChangeEvent } from "react";
import { Controller, FieldValues, useFormContext } from "react-hook-form";

import { FieldLabel } from "@/components/Form/Base/fieldLabel.component";
import { FormGroup } from "@/components/Form/Base/formGroup.component";
import { FormText } from "@/components/Form/Base/formText.component";
import { Input } from "@/components/Form/Base/input.component";
import { MicroCopy } from "@/components/Form/Base/microCopy.component";

import { FormFeedback } from "../../FormElements/formFeedback.component";
import { calculateFieldState } from "../../Utils/form.helpers";
import { FormValuesWithOptionalChildrenExplode } from "../formField.types";

type CheckBoxFieldProps<T extends FieldValues> =
    FormValuesWithOptionalChildrenExplode<T> & {
        optionalIndication?: boolean;
        requiredIndication?: boolean;
        disabled?: boolean;
        onChange?: (
            event: ChangeEvent<HTMLInputElement>,
            onChange: (event: ChangeEvent<HTMLInputElement>) => void
        ) => void;
    };

const CheckBoxField = <T extends FieldValues>({
    name,
    label,
    microCopy,
    required,
    hideLabel = false,
    hideMicroCopy = false,
    optionalIndication,
    requiredIndication,
    disabled,
    className,
    onChange,
    children
}: Omit<CheckBoxFieldProps<T>, "debounce" | "test" | "tooltip ">) => {
    const { control } = useFormContext<T>();

    return (
        <Controller<T>
            control={control}
            name={name}
            render={({ field, fieldState, formState }) => {
                const newFieldState = calculateFieldState<T>(
                    fieldState,
                    formState
                );

                const handleOnChange = (
                    event: ChangeEvent<HTMLInputElement>
                ) => {
                    return onChange
                        ? onChange(event, field.onChange)
                        : field.onChange(event);
                };

                return (
                    <FormGroup
                        toggle={true}
                        className={cn(className, "tw:flex tw:flex-col")}
                    >
                        <FieldLabel
                            name={name}
                            hideLabel={hideLabel}
                            label={label}
                            required={required}
                            optionalIndication={optionalIndication}
                            requiredIndication={requiredIndication}
                            className={"tw:cursor-pointer"}
                        >
                            <Input
                                {...field}
                                type={"checkbox"}
                                onChange={handleOnChange}
                                id={name}
                                checked={field.value}
                                valid={newFieldState.isValid}
                                invalid={newFieldState.isInvalid}
                                disabled={disabled}
                            />
                            {children
                                ? children({
                                      field,
                                      fieldState: newFieldState
                                  })
                                : label}
                        </FieldLabel>
                        <div className={cn("tw:mt-1")}>
                            {!hideMicroCopy &&
                                microCopy &&
                                (microCopy instanceof Function ? (
                                    microCopy()
                                ) : (
                                    <FormText
                                        inline={true}
                                        color="default"
                                    >
                                        {microCopy}
                                    </FormText>
                                ))}
                            {!hideMicroCopy && !microCopy && (
                                <MicroCopy
                                    className={cn("tw:mb-0")}
                                    fieldId={name}
                                />
                            )}
                        </div>
                        <FormFeedback
                            name={name}
                            label={label}
                        />
                    </FormGroup>
                );
            }}
        />
    );
};

export { CheckBoxField };
